import React from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import ReactECharts from 'echarts-for-react';
import { FiCheckCircle } from 'react-icons/fi';
import { CHART_FONT, CHART_TOOLTIP, CHART_SEMANTIC } from 'helpers/echartsTheme';

const fmtM = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};

const BAR_DONE   = CHART_SEMANTIC.success;
const BAR_ACTIVE = CHART_SEMANTIC.info;

const buildMonthlyOption = (monthly) => ({
  textStyle: { fontFamily: CHART_FONT.fontFamily },
  grid: { left: 0, right: 0, top: 8, bottom: 0, containLabel: true },
  tooltip: {
    ...CHART_TOOLTIP,
    trigger: 'item',
    formatter: (params) => {
      const m = monthly[params.dataIndex];
      if (!m) return '';
      return `${m.period}: ${m.count} completed (${fmtM(m.value)})`;
    }
  },
  xAxis: {
    type: 'category',
    axisLine: { show: false },
    axisTick: { show: false },
    data: monthly.map((m) => m.period?.split(' ')[0]),
    axisLabel: { fontSize: 9, color: '#9CA3AF', interval: 0 }
  },
  yAxis: { type: 'value', show: false },
  series: [{
    type: 'bar',
    barCategoryGap: '10%',
    itemStyle: { color: BAR_DONE, borderRadius: [2, 2, 0, 0] },
    emphasis: {
      itemStyle: { color: BAR_DONE, opacity: 0.8 },
      label: { show: true, position: 'top', fontSize: 11, color: '#6B7280' }
    },
    label: { show: false },
    data: monthly.map((m) => m.count)
  }]
});

const CompletionChart = ({ data, loading, error, onDrillDown }) => {
  if (loading) return (
    <Card className="tw-p-6 tw-border tw-border-gray-200">
      <div className="tw-flex tw-justify-center tw-py-12"><LoadingSpinner size="lg" /></div>
    </Card>
  );
  if (error) return (
    <Card className="tw-p-4 tw-bg-red-50 tw-border tw-border-red-200">
      <p className="tw-text-red-600 tw-text-sm">Error loading completion data</p>
    </Card>
  );

  const d              = data || {};
  const total          = d.total_jobs      || 0;
  const completedCnt   = d.completed_count || 0;
  const activeCnt      = d.active_count    || 0;
  const rate           = d.completion_rate || 0;
  const monthly        = d.monthly_trend   || [];

  const completedPct = total > 0 ? (completedCnt / total) * 100 : 0;
  const activePct    = total > 0 ? (activeCnt    / total) * 100 : 0;

  return (
    <Card className="tw-border tw-border-gray-200 tw-bg-gradient-to-br tw-from-emerald-50 tw-via-white tw-to-blue-50 tw-shadow-sm">
      <div className="tw-p-5 tw-border-b tw-border-gray-200">
        <h3 className="tw-font-bold tw-text-gray-900 tw-flex tw-items-center tw-gap-2">
          <FiCheckCircle className="tw-text-emerald-600" size={18} />
          Job Completion Analysis
        </h3>
        <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5">Completed (delivered) vs jobs still being processed</p>
      </div>

      <div className="tw-p-5 tw-space-y-5">
        {/* KPI row */}
        <div className="tw-grid tw-grid-cols-3 tw-gap-3">
          <div className="tw-text-center tw-bg-gray-50 tw-rounded-lg tw-p-3">
            <p className="tw-text-2xl tw-font-extrabold tw-text-gray-900">{total}</p>
            <p className="tw-text-xs tw-text-gray-500">Total Jobs</p>
          </div>
          <button
            type="button"
            disabled={completedCnt === 0 || !onDrillDown}
            onClick={() => onDrillDown && completedCnt > 0 && onDrillDown(
              `Completed Jobs (${completedCnt})`,
              { completion_status: 'completed' }
            )}
            className="tw-text-center tw-bg-emerald-50 tw-rounded-lg tw-p-3 tw-border-0 tw-cursor-pointer hover:tw-shadow-md tw-transition-all disabled:tw-cursor-default disabled:tw-opacity-70"
          >
            <p className="tw-text-2xl tw-font-extrabold tw-text-emerald-600">{completedCnt}</p>
            <p className="tw-text-xs tw-text-emerald-700">Completed</p>
            <p className="tw-text-xs tw-text-gray-400">{fmtM(d.completed_value)}</p>
          </button>
          <button
            type="button"
            disabled={activeCnt === 0 || !onDrillDown}
            onClick={() => onDrillDown && activeCnt > 0 && onDrillDown(
              `In-Progress Jobs (${activeCnt})`,
              { completion_status: 'active' }
            )}
            className="tw-text-center tw-bg-blue-50 tw-rounded-lg tw-p-3 tw-border-0 tw-cursor-pointer hover:tw-shadow-md tw-transition-all disabled:tw-cursor-default disabled:tw-opacity-70"
          >
            <p className="tw-text-2xl tw-font-extrabold tw-text-blue-600">{activeCnt}</p>
            <p className="tw-text-xs tw-text-blue-700">In Progress</p>
            <p className="tw-text-xs tw-text-gray-400">{fmtM(d.active_value)}</p>
          </button>
        </div>

        {/* Progress bar */}
        <div>
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-1.5">
            <span className="tw-text-xs tw-text-gray-500">Completion Rate</span>
            <span className="tw-text-sm tw-font-extrabold tw-text-emerald-600">{rate}%</span>
          </div>
          <div className="tw-h-3 tw-bg-gray-100 tw-rounded-xl tw-overflow-hidden tw-flex">
            <div style={{ width: `${completedPct}%`, backgroundColor: BAR_DONE    }} className="tw-h-full tw-transition-all tw-duration-500" />
            <div style={{ width: `${activePct}%`,    backgroundColor: BAR_ACTIVE  }} className="tw-h-full tw-transition-all tw-duration-500" />
          </div>
          <div className="tw-flex tw-items-center tw-gap-4 tw-mt-1.5">
            <span className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-500">
              <span className="tw-w-2.5 tw-h-2.5 tw-rounded-sm tw-inline-block" style={{ background: BAR_DONE }} />
              Completed {completedPct.toFixed(0)}%
            </span>
            <span className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-500">
              <span className="tw-w-2.5 tw-h-2.5 tw-rounded-sm tw-inline-block" style={{ background: BAR_ACTIVE }} />
              In Progress {activePct.toFixed(0)}%
            </span>
          </div>
        </div>

        {/* Monthly completion trend */}
        {monthly.length > 0 && (
          <div>
            <p className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-2">
              Monthly Completions
            </p>
            <ReactECharts
              option={buildMonthlyOption(monthly)}
              style={{ height: 100, width: '100%' }}
              notMerge
            />
          </div>
        )}
      </div>
    </Card>
  );
};

CompletionChart.propTypes = {
  data:        PropTypes.object,
  loading:     PropTypes.bool,
  error:       PropTypes.any,
  onDrillDown: PropTypes.func
};
CompletionChart.defaultProps = { data: null, loading: false, error: null, onDrillDown: null };

export default CompletionChart;
